import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Button, Checkbox, Group, Stack, Text, Textarea } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconPaperclip } from '@tabler/icons-react';

import { CONVERSATION_ATTACHMENT_MIME, ConversationAttachment } from '@/types/attachment';
import { ChatSendRequest } from '@/types/chat';
import { AttachmentRemoveButton, MessageAttachment } from './MessageAttachment';

/** onSend payload — the optional `attachmentIds` keeps callers that don't
 *  support attachments (e.g. the aside ChatPanel) source-compatible. */
export type ComposerSendRequest = ChatSendRequest & { attachmentIds?: string[] };

interface ComposerProps {
  /** Whether the composer should expose the "internal note" checkbox.
   * Analysts only — clients can't send private notes (the API also
   * rejects them, but we hide the toggle for clarity). */
  canSendPrivate: boolean;
  onSend: (body: ComposerSendRequest) => Promise<void>;
  isSending: boolean;
  /** Disables the textarea + button (e.g. while the parent is still
   * fetching the thread, or the thread is read-only). */
  disabled?: boolean;
  /** When provided, the composer exposes an attach affordance (paperclip +
   * drag-drop) and uploads files via this callback before send. Omit to get
   * the plain text-only composer (the aside ChatPanel behaviour). */
  onUploadAttachment?: (file: File) => Promise<ConversationAttachment[] | null>;
  /** Seed the textarea once when this value becomes non-empty (e.g. a
   * canned message). Re-seeds whenever the value changes to a new
   * non-empty string; the analyst can edit before sending. */
  seedText?: string;
  /** Re-seeds with the same `seedText` whenever this changes: a template
   * chosen again after the text was edited. */
  seedKey?: string | number;
  /** The longest message the destination accepts; shown as an "n/max"
   * counter under the box, and enforced by it. */
  maxLength?: number;
  /** Rendered above the textarea: what the message answers, canned text,
   * options that go with it. */
  header?: ReactNode;
}

/** Multi-line composer with optional internal-note toggle + file attachments.
 * Cmd/Ctrl-Enter submits; the explicit Send button is for less keyboard-fluent
 * users. Files are uploaded first (chips appear), then referenced on send. */
export function Composer({
  canSendPrivate,
  onSend,
  isSending,
  disabled,
  onUploadAttachment,
  seedText,
  seedKey,
  maxLength,
  header,
}: Readonly<ComposerProps>) {
  const [text, setText] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [pending, setPending] = useState<ConversationAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const openRef = useRef<() => void>(null);

  // Seed the textarea when a non-empty seedText arrives (or changes to a
  // new value, or the same value under a new key) — e.g. "Insert canned
  // offer". Tracked by a ref so the text is only overwritten on an actual
  // change, never on every keystroke re-render.
  const lastSeedRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const stamp = seedText ? `${seedKey ?? ''}\u0000${seedText}` : undefined;
    if (seedText && stamp !== lastSeedRef.current) {
      lastSeedRef.current = stamp;
      setText(seedText);
    }
  }, [seedText, seedKey]);

  const canAttach = Boolean(onUploadAttachment);
  const busy = disabled || isSending || isUploading;

  async function handleDrop(files: File[]) {
    if (!onUploadAttachment) {
      return;
    }
    setIsUploading(true);
    try {
      for (const file of files) {
        const created = await onUploadAttachment(file);
        if (created) {
          setPending((prev) => [...prev, ...created]);
        }
      }
    } finally {
      setIsUploading(false);
    }
  }

  async function handleSend() {
    const trimmed = text.trim();
    if (!trimmed && pending.length === 0) {
      return;
    }
    try {
      await onSend({
        // Attachment-only sends still need an email body (and an empty
        // bubble reads oddly) — fall back to a standard line.
        bodyText: trimmed || 'Please see attached.',
        isPrivate: canSendPrivate && isPrivate,
        attachmentIds: pending.length ? pending.map((a) => a.id) : undefined,
      });
    } catch {
      // The parent reports the failure; the text stays for another attempt.
      return;
    }
    setText('');
    setIsPrivate(false);
    setPending([]);
  }

  return (
    <Stack gap="xs">
      {pending.length > 0 && (
        <Stack gap={4}>
          {pending.map((att) => (
            <MessageAttachment
              key={att.id}
              attachment={att}
              compact
              action={
                <AttachmentRemoveButton
                  onRemove={() => setPending((prev) => prev.filter((p) => p.id !== att.id))}
                />
              }
            />
          ))}
        </Stack>
      )}
      {canAttach && (
        <Dropzone
          openRef={openRef}
          onDrop={handleDrop}
          accept={CONVERSATION_ATTACHMENT_MIME}
          multiple
          activateOnClick={false}
          p={0}
          style={{ border: 'none', background: 'transparent' }}
        >
          {/* Drag-and-drop is enabled across this zone; the visible trigger
              is the paperclip button below (activateOnClick disabled so the
              textarea isn't swallowed by the dropzone click target). */}
          <span />
        </Dropzone>
      )}
      {header}
      <Textarea
        autosize
        minRows={2}
        maxRows={6}
        placeholder="Type a message…"
        maxLength={maxLength}
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            void handleSend();
          }
        }}
        disabled={busy}
      />
      <Group justify="space-between">
        <Group gap="xs">
          {canAttach && (
            <Button
              variant="subtle"
              size="compact-sm"
              leftSection={<IconPaperclip size={16} />}
              onClick={() => openRef.current?.()}
              disabled={busy}
              loading={isUploading}
            >
              Attach
            </Button>
          )}
          {canSendPrivate && (
            <Checkbox
              label="Internal note (analyst-only)"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.currentTarget.checked)}
              disabled={busy}
            />
          )}
          {maxLength !== undefined && (
            <Text size="xs" c="dimmed" data-testid="composer-length">
              {text.length}/{maxLength}
            </Text>
          )}
        </Group>
        <Button
          onClick={handleSend}
          loading={isSending}
          disabled={busy || (!text.trim() && pending.length === 0)}
        >
          Send
        </Button>
      </Group>
    </Stack>
  );
}
